import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { ApIcon } from '@alixpartners/ui-components';
import { NavigationWrapper } from '../NavigationWrapper';
import { PublishingProgress } from '../../../components/PublishingProgress';
import { DeleteAllDataModal } from '../../../components/DeleteAllDataModal';
import { useAppDispatch, useAppSelector } from '../../../state';
import {
  selectFilesState,
  fetchAllFiles,
  refetchAllFiles,
  deleteAllFiles,
} from '../../../state/slices/filesSlice';
import {
  ACCOUNTS_PAYABLE,
  ACCOUNTS_RECEIVABLES,
  INVENTORY,
  STAGED,
  TFileSection,
  TPublishStatus,
} from '../../../types';
import {
  removeSession,
  removeSessionFromFxr,
  triggerEtlPublish,
  fetchEtlStatus,
} from '../../../utils/api';
import { later } from '../../../utils/later';
import { useDeleteHook } from '../UploadMapping';
import {
  PageWrapper,
  PageTitleBlock,
  PageTitle,
  PageSubtitle,
  NotesSection,
  NotesLabel,
  NotesRow,
  InfoBanner,
  Card,
  CardHeader,
  CardTitleBlock,
  CardTitle,
  CardSubtitle,
  CardActions,
  Divider,
  TableWrapper,
  TableHeaderRow,
  ColHeader,
  TableRow,
  FileIconContainer,
  FileNameCell,
  FileName,
  CellText,
  ActionsCell,
  TableLoadingRow,
  TableEmptyRow,
  SecondaryDangerButton,
  RowIconButton,
  ProgressError,
  UpdateReportsButton,
} from './CompletedMappings.styles';

const SECTION_DISPLAY_NAMES: Record<TFileSection, string> = {
  'accounts-payable': 'Accounts Payable',
  'accounts-receivables': 'Accounts Receivable',
  inventory: 'Inventory',
};

const PROGRESS_BY_STATUS: Record<TPublishStatus, number> = {
  None: 0,
  Queued: 10,
  Started: 50,
  Completed: 100,
  Failed: 0,
};

export const CompletedMappings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { userFiles, otherFiles, isFetching, isDeleting } =
    useAppSelector(selectFilesState);
  const [publishStatus, setPublishStatus] = useState<TPublishStatus>('None');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [pendingDeleteSessionId, setPendingDeleteSessionId] = useState<
    string | null
  >(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    dispatch(fetchAllFiles());
  }, []);

  useEffect(() => {
    if (publishStatus === 'Completed') {
      window.location.href = '/data-manager/reports';
    }
  }, [publishStatus]);

  const stagedRows = [
    ACCOUNTS_PAYABLE,
    ACCOUNTS_RECEIVABLES,
    INVENTORY,
  ].flatMap((section) => [
    ...Object.entries(userFiles[section][STAGED]).flatMap(([, files]) =>
      files.map((file) => file)
    ),
    ...otherFiles[section].filter((f) => f.status === STAGED),
  ]);

  const isPublishing =
    publishStatus === 'Queued' || publishStatus === 'Started';
  const progress = PROGRESS_BY_STATUS[publishStatus] ?? 0;

  const poll = async () => {
    if (stoppedRef.current) return;
    try {
      const { status } = await fetchEtlStatus();
      setPublishStatus(status);
      if (status !== 'Completed' && status !== 'Failed') {
        await later(5000);
        poll();
      }
    } catch {
      setPublishStatus('Failed');
    }
  };

  const handleUpdateReports = async () => {
    stoppedRef.current = false;
    setPublishStatus('Queued');
    try {
      await triggerEtlPublish();
    } catch {
      setPublishStatus('Failed');
      return;
    }
    poll();
  };

  const handleCancelPublish = () => {
    stoppedRef.current = true;
    setPublishStatus('None');
  };

  const handleDeleteMapped = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalSubmit = () => {
    dispatch(deleteAllFiles());
    setIsDeleteModalOpen(false);
  };

  const [handleDeleteRow] = useDeleteHook(
    async (sessionId: string) => {
      await removeSession(sessionId);
      await removeSessionFromFxr(sessionId);
    },
    () => {
      dispatch(refetchAllFiles());
    }
  );

  return (
    <>
      <NavigationWrapper hideStatusMessage={isPublishing} />
      {isPublishing ? (
        <PublishingProgress
          title="Updating reports"
          subtitle="Push your latest mapped data to reports. This will replace the existing version."
          progress={progress}
          onCancel={handleCancelPublish}
          message="Mapped files are currently being published to reports"
        />
      ) : (
        <PageWrapper>
          <PageTitleBlock>
            <PageTitle>Data ready for reports</PageTitle>
            <PageSubtitle>
              Push your latest mapped data to reports. This will replace the
              existing version.
            </PageSubtitle>
          </PageTitleBlock>

          <NotesSection>
            <NotesLabel>IMPORTANT NOTES</NotesLabel>
            <NotesRow>
              <InfoBanner>
                <ApIcon iconName="info_outline" iconSize={20} />
                Data updates overwrite previous reports
              </InfoBanner>
              <InfoBanner>
                <ApIcon iconName="info_outline" iconSize={20} />
                Up to 8 publishing sessions per day
              </InfoBanner>
              <InfoBanner>
                <ApIcon iconName="info_outline" iconSize={20} />
                Updating reports may take a few minutes
              </InfoBanner>
            </NotesRow>
          </NotesSection>

          <Card>
            <CardHeader>
              <CardTitleBlock>
                <CardTitle>Data ready for reports</CardTitle>
                <CardSubtitle>
                  Publish your mapped files to the Reports tab.
                </CardSubtitle>
              </CardTitleBlock>
              <CardActions>
                <UpdateReportsButton
                  disabled={stagedRows.length === 0}
                  onClick={handleUpdateReports}
                >
                  Update reports
                </UpdateReportsButton>
                <Divider />
                <SecondaryDangerButton onClick={handleDeleteMapped}>
                  Delete mapped data
                </SecondaryDangerButton>
              </CardActions>
            </CardHeader>

            {publishStatus === 'Failed' && (
              <ProgressError>
                Publishing failed. Please try again.
              </ProgressError>
            )}
            <TableWrapper>
              <TableHeaderRow>
                <ColHeader fillWidth>File Name</ColHeader>
                <ColHeader width="213px">File type</ColHeader>
                <ColHeader width="213px">Created by</ColHeader>
                <ColHeader width="213px">Created on</ColHeader>
                <ColHeader alignRight>Actions</ColHeader>
              </TableHeaderRow>

              {isFetching && (
                <TableLoadingRow>Loading mapped data...</TableLoadingRow>
              )}
              {!isFetching && stagedRows.length === 0 && (
                <TableEmptyRow>
                  No completed mappings yet — Upload files to start mapping and
                  generate report-ready data.
                </TableEmptyRow>
              )}
              {!isFetching &&
                stagedRows.map((row) => (
                  <TableRow key={row.id}>
                    <FileNameCell>
                      <FileIconContainer>
                        <ApIcon
                          iconName="outline_insert_drive_file"
                          iconSize={20}
                        />
                      </FileIconContainer>
                      <FileName>{row.name}</FileName>
                    </FileNameCell>
                    <CellText width="213px">
                      {SECTION_DISPLAY_NAMES[row.section]}
                    </CellText>
                    <CellText width="213px">{row.userName}</CellText>
                    <CellText width="213px">
                      {moment(row.uploadDate).format('MM/DD/YYYY')}
                    </CellText>
                    <ActionsCell>
                      <RowIconButton
                        onClick={() => setPendingDeleteSessionId(row.sessionId)}
                      >
                        <ApIcon
                          iconName="delete"
                          iconColor="#cb282e"
                          iconSize={20}
                        />
                      </RowIconButton>
                    </ActionsCell>
                  </TableRow>
                ))}
            </TableWrapper>
          </Card>
        </PageWrapper>
      )}
      {isDeleteModalOpen && (
        <DeleteAllDataModal
          onSubmit={handleDeleteModalSubmit}
          onCancel={() => setIsDeleteModalOpen(false)}
          isLoading={isDeleting}
          actionPhrase="delete mapped data"
        />
      )}
      {pendingDeleteSessionId && (
        <DeleteAllDataModal
          onSubmit={() => {
            handleDeleteRow(pendingDeleteSessionId);
            setPendingDeleteSessionId(null);
          }}
          onCancel={() => setPendingDeleteSessionId(null)}
          actionPhrase="delete mapped data"
        />
      )}
    </>
  );
};

export default CompletedMappings;
